/**
 * Pure derivation helpers for `Message.tsx`. Kept separate so they can be
 * unit-tested without spinning up React.
 */

export interface UserLike {
  id: string;
  display_name: string;
  avatar_url?: string | null;
}

export interface ReactionLike {
  user_id: string;
  emoji: string;
}

export interface ReactionGroup {
  count: number;
  users: string[];
  hasOwn: boolean;
  /**
   * The exact display-name string the viewer was pushed into `users` as
   * (could be "Unknown" if the displayNameMap missed them). Captured
   * during the same iteration that builds `users` so the tooltip's
   * "You"-hoist works even when the viewer's userMap entry is transiently
   * missing — without this, the viewer would see "Unknown" instead of
   * "You" in their own reactions. Null when the viewer didn't react.
   */
  viewerEntry: string | null;
}

/**
 * Build a quick id → display_name lookup from the renderer's userMap.
 * Returns an empty Map when no userMap was provided.
 */
export function buildDisplayNameMap(
  userMap: Map<string, UserLike> | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  if (!userMap) return map;
  for (const [id, u] of userMap) {
    map.set(id, u.display_name);
  }
  return map;
}

/**
 * Mention-resolvable user list. `workspaceMembers` is permissions-filtered
 * (guests / non-members can be absent), so we merge in every user known
 * to the renderer. Without this, @Mentions of guest users or of someone
 * only reachable via a shared channel render as plain text.
 */
export function buildMentionableUsers(
  workspaceMembers: { id: string; display_name: string }[] | undefined,
  userMap: Map<string, UserLike> | undefined,
): { id: string; display_name: string }[] {
  const seen = new Set<string>();
  const merged: { id: string; display_name: string }[] = [];
  for (const m of workspaceMembers ?? []) {
    if (!seen.has(m.id)) {
      seen.add(m.id);
      merged.push(m);
    }
  }
  if (userMap) {
    for (const [id, u] of userMap) {
      if (!seen.has(id) && u.display_name) {
        seen.add(id);
        merged.push({ id, display_name: u.display_name });
      }
    }
  }
  return merged;
}

/**
 * Group reactions by emoji. Each group records how many users reacted, the
 * display names that did so (used for the hover title), and whether the
 * current viewer reacted (used to highlight the pill).
 */
export function groupReactionsByEmoji(
  reactionList: readonly ReactionLike[],
  displayNameMap: Map<string, string>,
  viewerId: string | undefined,
): Map<string, ReactionGroup> {
  const groups = new Map<string, ReactionGroup>();
  for (const r of reactionList) {
    const group = groups.get(r.emoji) || {
      count: 0,
      users: [],
      hasOwn: false,
      viewerEntry: null,
    };
    group.count++;
    const name = displayNameMap.get(r.user_id) || "Unknown";
    group.users.push(name);
    if (r.user_id === viewerId) {
      group.hasOwn = true;
      group.viewerEntry = name;
    }
    groups.set(r.emoji, group);
  }
  return groups;
}
